import { useEffect, useRef, useState } from "react";
import Header from "@/components/sections/Header";
import Hero from "@/components/sections/Hero";
import { ProblemSection, BenefitsSection, WhoForSection, ProcessSection } from "@/components/sections/MidSections";
import TestimonialsSection from "@/components/sections/Testimonials";
import PricingSection from "@/components/sections/PricingSection";
import { IncludedExcludedSection, ChannelFitSection, WhyRekisaSection } from "@/components/sections/InfoSections";
import { FAQSection, FinalCTASection, AEOAnswerBlocks } from "@/components/sections/FAQFinalCTA";
import QuoteForm from "@/components/sections/QuoteForm";
import Footer from "@/components/sections/Footer";
import AuditPopup from "@/components/AuditPopup";
import AuditAnnouncement from "@/components/AuditAnnouncement";
import SectionRail from "@/components/sections/SectionRail";
import ReadingProgress from "@/components/sections/ReadingProgress";
import { trackEvent } from "@/lib/tracking";
import { usePageMeta } from "@/lib/meta";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "serviceType": "Paid Ads Management",
      "provider": { "@id": "https://campaigns.rekisa.co.za/#business" },
      "areaServed": { "@type": "Country", "name": "South Africa" },
      "name": "Paid Ads Management",
      "url": "https://campaigns.rekisa.co.za/paid-ads-management",
      "description":
        "Rekisa provides paid ads management for South African businesses across Google Ads, Meta Ads, LinkedIn Ads and remarketing channels. The service includes campaign strategy, setup, targeting, tracking, reporting and monthly optimisation.",
      "offers": [
        { "@type": "Offer", "name": "DWY Paid Ads Enablement", "price": "4850", "priceCurrency": "ZAR" },
        { "@type": "Offer", "name": "DFY Paid Ads Management", "price": "8890", "priceCurrency": "ZAR" },
        { "@type": "Offer", "name": "Growth Paid Ads System", "price": "13500", "priceCurrency": "ZAR" }
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Do you only run Google Ads?", "acceptedAnswer": { "@type": "Answer", "text": "No. Rekisa helps businesses run paid ads across Google, Facebook, Instagram, LinkedIn, and remarketing channels. The right platform depends on your offer, audience, budget, and sales goal." } },
        { "@type": "Question", "name": "Do I need a landing page?", "acceptedAnswer": { "@type": "Answer", "text": "In most cases, yes. Paid ads perform better when the landing page matches the ad message and gives the visitor one clear action to take." } },
        { "@type": "Question", "name": "Do you offer WhatsApp follow-up?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, but WhatsApp is only one option. Rekisa can help set up automated follow-up through WhatsApp, email, CRM workflows, SMS, or another structure that fits the client's sales process." } },
        { "@type": "Question", "name": "Who pays the advertising budget?", "acceptedAnswer": { "@type": "Answer", "text": "The client pays the advertising budget directly to the advertising platform. Rekisa charges for strategy, setup, management, optimisation, tracking, and reporting." } },
        { "@type": "Question", "name": "Can you help if we already run ads?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Rekisa can review existing campaigns, tracking, landing pages, audiences, budgets, and lead quality before recommending what needs to be fixed." } },
        { "@type": "Question", "name": "Do you guarantee sales?", "acceptedAnswer": { "@type": "Answer", "text": "No. Sales depend on the offer, pricing, market demand, sales process, budget, and follow-up. Rekisa focuses on proper setup, tracking, testing, optimisation, and lead quality improvement." } },
        { "@type": "Question", "name": "What is remarketing?", "acceptedAnswer": { "@type": "Answer", "text": "Remarketing means showing ads to people who have already interacted with your business, such as website visitors, previous leads, video viewers, or people who engaged with your social pages." } }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://campaigns.rekisa.co.za/" },
        { "@type": "ListItem", "position": 2, "name": "Paid Ads Management", "item": "https://campaigns.rekisa.co.za/paid-ads-management" }
      ]
    }
  ]
};

export default function PaidAds() {
  usePageMeta({
    title: "Paid Ads Management South Africa | Rekisa",
    description:
      "Get more leads and sales with paid ads across Google, Facebook, Instagram and LinkedIn. Rekisa runs strategy, campaigns, remarketing, tracking and monthly optimisation for South African businesses.",
    canonical: "https://campaigns.rekisa.co.za/paid-ads-management",
    jsonLd: JSON_LD,
  });

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [addons, setAddons] = useState({
    landing_page: false,
    followup: false,
    whatsapp: false,
    extra_platform: false,
  });

  const scrollMarks = useRef({ 50: false, 75: false, 90: false });
  const pricingViewed = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      [50, 75, 90].forEach((mark) => {
        if (pct >= mark && !scrollMarks.current[mark]) {
          scrollMarks.current[mark] = true;
          trackEvent(`scroll_${mark}`);
        }
      });
      if (!pricingViewed.current) {
        const el = document.getElementById("pricing");
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            pricingViewed.current = true;
            trackEvent("pricing_section_view");
          }
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-white text-zinc-900">
      <AuditAnnouncement service="paid_ads" />
      <Header />
      <ReadingProgress
        sections={[
          { id: "hero-section", label: "Top" },
          { id: "benefits-section", label: "Why us" },
          { id: "testimonials-section", label: "Clients" },
          { id: "process-section", label: "Process" },
          { id: "pricing-section", label: "Pricing" },
          { id: "faq-section", label: "FAQ" },
          { id: "quote-form-section", label: "Quote" },
        ]}
      />
      <SectionRail
        service="paid_ads"
        sections={[
          { id: "hero-section", label: "Top" },
          { id: "benefits-section", label: "Why us" },
          { id: "testimonials-section", label: "Clients" },
          { id: "process-section", label: "Process" },
          { id: "pricing-section", label: "Pricing" },
          { id: "faq-section", label: "FAQ" },
          { id: "quote-form-section", label: "Quote" },
        ]}
      />
      <main>
        <Hero />
        <ProblemSection />
        <BenefitsSection />
        <TestimonialsSection />
        <WhoForSection />
        <ProcessSection />
        <PricingSection
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          addons={addons}
          setAddons={setAddons}
        />
        <IncludedExcludedSection />
        <ChannelFitSection />
        <WhyRekisaSection />
        <FAQSection />
        <AEOAnswerBlocks />
        <FinalCTASection />
        <QuoteForm
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          addons={addons}
          setAddons={setAddons}
        />
      </main>
      <Footer />
      <AuditPopup service="paid_ads" />
    </div>
  );
}
